import {
  EmptyState,
  FieldAlert,
  FlexBox,
  InputSelectionListProps,
  SelectionList,
  SimplePagination,
  Text,
  TextWithHighlight,
} from '@dixa/lib-shared-habla';
import {
  HookFormFieldWrappedBaseProps,
  HookFormSelectionList,
} from 'lib-shared-hook-form';
import { AsyncStatus } from 'lib-shared-types-api';
import { useEffect, useState } from 'react';
import { FieldPath, FieldValues, useWatch } from 'react-hook-form';

import { integrationsApi } from 'apis/integrations';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';

import styles from './hookFormEmailSelect.module.scss';

type HookFormEmailSelectProps = Partial<
  Omit<InputSelectionListProps<string>, 'children'>
> & {
  onEmailAsyncStatusChange?: (status: AsyncStatus) => void;
};

export const HookFormEmailSelect = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: HookFormFieldWrappedBaseProps<
    TFieldValues,
    TName,
    HookFormEmailSelectProps
  >,
): JSX.Element => {
  const { onEmailAsyncStatusChange, ...restComponentProps } =
    props.componentProps ?? {};
  const [filter, setFilter] = useState('');

  const {
    items: emails,
    asyncStatus: getEmailAsyncStatus,
    changeArg,
    nextPage,
    previousPage,
    hasNextPage,
    hasPreviousPage,
  } = usePaginatedEndpoint({
    endpoint: integrationsApi.endpoints.readEmailIntegrations,
    itemsSelector: ({ data: { emails } }) => emails || [],
    initialArg: {
      limit: 50,
      offset: 0,
      sort: { key: 'email_address', direction: 'asc' },
    },
  });

  const selectedValue = useWatch({ control: props.control, name: props.name });
  const selectedEmailAddress = Array.isArray(selectedValue)
    ? (selectedValue[0] ?? null)
    : null;

  const { data: enrichedIntegrationData } =
    integrationsApi.useReadEmailIntegrationQuery(
      { emailAddress: selectedEmailAddress ?? '' },
      { skip: !selectedEmailAddress },
    );
  const enrichedIntegration = enrichedIntegrationData?.data;

  useEffect(() => {
    onEmailAsyncStatusChange?.(getEmailAsyncStatus);
  }, [getEmailAsyncStatus, onEmailAsyncStatusChange]);

  const hasNoEmails = !emails.length;
  const showNoIntegrationWarning =
    getEmailAsyncStatus === 'fulfilled' && hasNoEmails;

  return (
    <>
      <HookFormSelectionList
        {...props}
        rules={
          props.rules ?? {
            required: {
              value: true,
              message: 'Please select an email',
            },
          }
        }
        componentProps={{
          placeholder: 'Select email',
          getItemTitleByValue: (emailAddress) => {
            // Prefer the live list; fall back to the enriched single-integration
            // fetch which handles cases where the email isn't on the current page
            // (search reset, draft load, etc.).
            const integration =
              emails.find((e) => e.email_address === emailAddress) ??
              (enrichedIntegration?.email_address === emailAddress
                ? enrichedIntegration
                : undefined);
            const addressPart = integration?.spf_sender_email ? (
              <>
                {integration.spf_sender_email}
                <span className={styles.spfArrow}> → </span>
                {emailAddress}
              </>
            ) : (
              emailAddress
            );
            return (
              <Text.Paragraph>
                {integration?.sender_name ? (
                  <>
                    <strong>{integration.sender_name}</strong>
                    {' <'}
                    {addressPart}
                    {'>'}
                  </>
                ) : (
                  addressPart
                )}
              </Text.Paragraph>
            );
          },
          dropdownProps: {
            className: styles.selectDropdown,
          },
          filter: {
            value: filter,
            onChange: (event) => {
              const searchTerm = event.target.value;

              setFilter(searchTerm);

              changeArg(
                { searchTerm, offset: 0, sort: undefined },
                {
                  debounceTime: 300,
                },
              );
            },
          },
          onToggleDropdown: (isOpen: boolean) => {
            // When the user closes the dropdown with a previous
            // search term, we reset so the email list gets fetched
            // once again
            if (!isOpen && filter !== '') {
              setFilter('');

              changeArg({
                searchTerm: '',
                offset: 0,
                sort: { key: 'email_address', direction: 'asc' },
              });
            }
          },
          ...restComponentProps,
        }}
      >
        <>
          {emails.map((email) => (
            <SelectionList.Item
              key={email.email_address}
              value={email.email_address}
            >
              <Text.Paragraph>
                {email.sender_name && (
                  <>
                    <strong>
                      <TextWithHighlight
                        text={email.sender_name}
                        highlight={filter}
                      />
                    </strong>
                    {' <'}
                  </>
                )}
                {email.spf_sender_email ? (
                  <>
                    <TextWithHighlight
                      text={email.spf_sender_email}
                      highlight={filter}
                    />
                    <span className={styles.spfArrow}> → </span>
                    <TextWithHighlight
                      text={email.email_address}
                      highlight={filter}
                    />
                  </>
                ) : (
                  <TextWithHighlight
                    text={email.email_address}
                    highlight={filter}
                  />
                )}
                {email.sender_name && '>'}
              </Text.Paragraph>
            </SelectionList.Item>
          ))}

          {hasNoEmails && (
            <SelectionList.Item
              noFixedSelectionStateSpace
              className={styles.selectDropdownRow}
            >
              <EmptyState
                title="No items found"
                description="Try changing your search criteria."
              />
            </SelectionList.Item>
          )}

          <FlexBox className={styles.selectDropdownPagination}>
            <SimplePagination
              {...(getEmailAsyncStatus !== 'uninitialized' || emails.length > 0
                ? {
                    nextButtonClick: nextPage,
                    previousButtonClick: previousPage,
                    nextButtonDisabled:
                      !hasNextPage || getEmailAsyncStatus !== 'fulfilled',
                    previousButtonDisabled:
                      !hasPreviousPage || getEmailAsyncStatus !== 'fulfilled',
                  }
                : undefined)}
            />
          </FlexBox>
        </>
      </HookFormSelectionList>

      {showNoIntegrationWarning && (
        <FieldAlert variant="warning">No email integration found.</FieldAlert>
      )}
    </>
  );
};
